import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq, or } from 'tio-ui/utilities';

import { registerDestructor } from '@ember/destroyable';
import Drawer from 'tio-reimagined/components/drawer';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Button from 'tio-reimagined/components/button';
import FileUploader from 'tio-ui/components/file-uploader';
import { PhFile, PhXCircle, PhCheckCircle, PhWarning } from 'ember-phosphor-icons';
import type { UploadFile, Queue } from 'ember-file-upload';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import type { IntlService } from 'ember-intl';
import type PersonModel from 'tio-data/models/person';
import type Owner from '@ember/owner';

const ACCEPTED_FILE_TYPES = '.pdf';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_EXTENSIONS = ['pdf'];
const PROCESSING_TIMEOUT_MS = 60_000;

type UploadState = 'idle' | 'file-selected' | 'uploading' | 'processing' | 'success' | 'error';

type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};

type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
};

/**
 * DocumentUploadDrawer — Update Your Loan Account drawer.
 *
 * Matches the Figma design: title, 3-step servicer statement instructions,
 * "Can't find it?" help text, file upload dropzone (PDF, TXT, PNG, JPG), and Done footer.
 *
 * @example
 * ```gts
 * <DocumentUploadDrawer
 *   @isOpen={{this.isUploadDrawerOpen}}
 *   @onClose={{this.toggleUploadDrawer}}
 *   @person={{this.person}}
 * />
 * ```
 */
interface DocumentUploadDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Person whose documents are being uploaded */
    person: PersonModel;
  };
  Element: HTMLDivElement;
}

export default class DocumentUploadDrawer extends Component<DocumentUploadDrawerSignature> {
  @service declare observables: ObservablesService;
  @service declare cable: CableService;
  @service declare intl: IntlService;
  @service declare fileQueue: Queue;

  @tracked state: UploadState = 'idle';
  @tracked upload?: UploadFile;
  @tracked fileValidationError: string | null = null;
  @tracked uploadError: string | null = null;
  @tracked processingTimedOut = false;

  batchId = crypto.randomUUID();
  private subscriptionParams: object | null = null;
  private processingTimer?: ReturnType<typeof setTimeout>;

  constructor(owner: Owner, args: DocumentUploadDrawerSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => {
      this.unsubscribe();
      clearTimeout(this.processingTimer);
      this.clearQueue();
    });
  }

  get queue() {
    // @ts-expect-error findOrCreate exists on Queue
    return this.fileQueue.findOrCreate('update-loan-upload');
  }

  private unsubscribe = () => {
    if (this.subscriptionParams) {
      this.cable.unsubscribe(this.subscriptionParams);
      this.subscriptionParams = null;
    }
  };

  private subscribeToBatchStatus = () => {
    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: this.batchId,
    };

    this.subscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const msg = data as BatchStatus;
        const { id, recordAttributes, transition } = msg;

        this.observables.updateObservableDocument({ ...recordAttributes, id });

        const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
          recordAttributes.extractionState ?? ''
        );

        if (extractionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);
          const isInvalidDocument = recordAttributes.extractionState === 'ExtractionState.INVALID';
          this.uploadError = isInvalidDocument
            ? this.intl.t('observability.wizard.upload.error_invalid_document')
            : this.intl.t('observability.wizard.upload.error_failed');
          this.state = 'error';
          return;
        }

        const reflectionSuccessful = transition.event === 'content_reflected';
        const reflectionFailed = transition.event === 'reflection_failed';

        if (reflectionSuccessful || reflectionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);
          this.state = reflectionSuccessful ? 'success' : 'error';
          if (reflectionFailed) {
            this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
          }
        }
      },
    });

    this.processingTimer = setTimeout(() => {
      if (this.state === 'processing') {
        this.processingTimedOut = true;
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  handleFileAdded = (uploadFile: UploadFile) => {
    this.fileValidationError = null;
    this.uploadError = null;
    const file = uploadFile.file;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VALID_EXTENSIONS.includes(ext)) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.invalid_file_type');
      this.queue.remove(uploadFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.upload = uploadFile;
    this.state = 'file-selected';
  };

  removeFile = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.state = 'idle';
  };

  handleSubmit = async () => {
    if (!this.upload) return;

    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.processingTimedOut = false;
    this.state = 'uploading';
    this.subscribeToBatchStatus();

    try {
      await this.observables.createObservableDocument(this.upload, {
        person: this.args.person,
        batch: this.batchId,
      });
      this.state = 'processing';
    } catch {
      this.unsubscribe();
      clearTimeout(this.processingTimer);
      this.processingTimedOut = false;
      this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
      this.state = 'error';
    }
  };

  private clearQueue = () => {
    for (const file of [...this.queue.files]) {
      this.queue.remove(file);
    }
  };

  reset = () => {
    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.clearQueue();
    this.upload = undefined;
    this.fileValidationError = null;
    this.uploadError = null;
    this.processingTimedOut = false;
    this.state = 'idle';
    this.batchId = crypto.randomUUID();
  };

  handleClose = () => {
    this.reset();
    this.args.onClose();
  };

  handleDone = () => {
    if (this.state === 'file-selected') {
      this.handleSubmit();
    } else if (this.state === 'success') {
      this.handleClose();
    }
  };

  get isDoneDisabled() {
    return (
      this.state === 'idle' ||
      this.state === 'uploading' ||
      this.state === 'processing' ||
      this.state === 'error'
    );
  }

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @size="lg"
      @placement="right"
      ...attributes
    >
      <:header>
        <Heading @level={{3}}>
          {{t "slr.reimagined.update_loan_account_title"}}
        </Heading>
        <Text @size="sm" class="tio-fg-muted mt-1">
          {{t "slr.reimagined.update_loan_account_subtitle"}}
        </Text>
      </:header>
      <:body>
        <div class="tio-col-extra-loose">
          {{#if (eq this.state "success")}}
            {{! ── Success State ── }}
            <div class="rounded-xl tio-bg-success-soft border tio-border-success p-6 text-center">
              <div class="tio-row-center h-12 w-12 rounded-full tio-bg-success-soft mx-auto mb-3">
                <PhCheckCircle @size={{24}} class="tio-fg-success" />
              </div>
              <Heading @level={{3}} class="tio-fg-success-h mb-1">
                {{t "observability.wizard.complete.heading"}}
              </Heading>
              <Text @size="sm" class="tio-fg-success-body mb-4">
                {{t
                  "observability.wizard.complete.description"
                  docType=(t "observability.wizard.doc_type.statement")
                }}
              </Text>
              <button
                type="button"
                class="text-sm font-medium tio-fg-success-body underline hover:tio-fg-success-body"
                {{on "click" this.reset}}
              >
                {{t "observability.wizard.complete.upload_another"}}
              </button>
            </div>
          {{else}}
            {{! ── Instructions ── }}
            <div>
              <Heading @level={{4}} class="mb-4">
                {{t "slr.reimagined.how_to_find_statement"}}
              </Heading>

              <ol class="space-y-4">
                <li class="tio-flex">
                  <span
                    class="tio-row-center h-7 w-7 rounded-full bg-gray-900 tio-fg-inverse text-xs font-bold shrink-0 mt-0.5"
                  >{{1}}</span>
                  <div>
                    <Text>
                      <span class="font-semibold">{{t "slr.reimagined.step_find_title"}}</span>
                      {{t "slr.reimagined.step_find_description"}}
                    </Text>
                  </div>
                </li>
                <li class="tio-flex">
                  <span
                    class="tio-row-center h-7 w-7 rounded-full bg-gray-900 tio-fg-inverse text-xs font-bold shrink-0 mt-0.5"
                  >{{2}}</span>
                  <div>
                    <Text>
                      <span class="font-semibold">{{t "slr.reimagined.step_download_title"}}</span>
                      {{t "slr.reimagined.step_download_description"}}
                    </Text>
                  </div>
                </li>
                <li class="tio-flex">
                  <span
                    class="tio-row-center h-7 w-7 rounded-full bg-gray-900 tio-fg-inverse text-xs font-bold shrink-0 mt-0.5"
                  >{{3}}</span>
                  <div>
                    <Text>
                      <span class="font-semibold">{{t "slr.reimagined.step_verify_title"}}</span>
                      {{t "slr.reimagined.step_verify_description"}}
                    </Text>
                  </div>
                </li>
              </ol>
            </div>

            {{! ── Can't find it? ── }}
            <div>
              <Text>
                <span class="font-bold">{{t "slr.reimagined.cant_find_it"}}</span>
                {{t "slr.reimagined.cant_find_it_help"}}
              </Text>
            </div>

            {{! ── Upload Area ── }}
            <div>
              <FileUploader
                @name="update-loan-upload"
                @accept={{ACCEPTED_FILE_TYPES}}
                @onFileAdded={{this.handleFileAdded}}
                @useDropzone={{true}}
                @disabled={{if this.upload true false}}
              />
              {{#if this.upload}}
                <div class="tio-row-tight border tio-border-default rounded-lg px-3 py-2 mt-3">
                  <PhFile @size={{16}} class="tio-fg-muted shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary flex-1 truncate min-w-0">
                    {{this.upload.file.name}}
                  </Text>
                  <button
                    type="button"
                    class="p-1 rounded tio-fg-disabled hover:tio-fg-danger-body transition-colors shrink-0"
                    aria-label={{t "slr.reimagined.remove_selected_file"}}
                    {{on "click" this.removeFile}}
                  >
                    <PhXCircle @size={{16}} />
                  </button>
                </div>
              {{else}}
                <Text @size="sm" class="text-center tio-fg-muted mt-1">
                  {{t "slr.reimagined.supported_format_pdf"}}
                </Text>
              {{/if}}

              {{! Progress bar – inline per Figma (visible during uploading/processing) }}
              {{#if (or (eq this.state "uploading") (eq this.state "processing"))}}
                <div class="tio-row-tight w-full px-1 mt-1" data-test-document-upload-progress-bar>
                  <Text @weight="medium" class="text-[10px] tio-fg-secondary whitespace-nowrap">
                    {{#if (eq this.state "uploading")}}
                      {{t "observability.wizard.upload.uploading"}}
                    {{else}}
                      {{t "observability.wizard.upload.processing_title"}}
                    {{/if}}
                  </Text>
                  <div class="flex-1 h-1 bg-black/10 rounded-2xl overflow-hidden">
                    <div class="h-full w-2/3 tio-bg-info rounded-2xl animate-pulse"></div>
                  </div>
                </div>
                {{#if this.processingTimedOut}}
                  <div
                    class="tio-bg-warning-soft border tio-border-warning rounded-lg p-3 tio-row-top-tight w-full"
                  >
                    <PhWarning @size={{16}} class="tio-fg-warning shrink-0 mt-0.5" />
                    <Text @size="xs" class="tio-fg-warning-h">
                      {{t "observability.wizard.upload.processing_slow"}}
                    </Text>
                  </div>
                {{/if}}
              {{/if}}
            </div>

            {{! Validation error }}
            {{#if this.fileValidationError}}
              <div
                class="rounded-xl border tio-border-danger tio-bg-danger-soft p-3 tio-row-top-tight"
              >
                <PhWarning @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                <Text @size="xs" class="tio-fg-danger-h">{{this.fileValidationError}}</Text>
              </div>
            {{/if}}

            {{! Upload error }}
            {{#if (eq this.state "error")}}
              <div
                class="rounded-xl border tio-border-danger tio-bg-danger-soft p-3 tio-row-top-tight"
              >
                <PhWarning @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                <div>
                  <Text
                    @size="xs"
                    @weight="medium"
                    class="tio-fg-danger-h"
                  >{{this.uploadError}}</Text>
                  <button
                    type="button"
                    class="text-xs tio-fg-danger-body underline mt-1"
                    {{on "click" this.reset}}
                  >
                    {{t "observability.wizard.upload.try_again"}}
                  </button>
                </div>
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-end">
          <Button
            @intent="primary"
            @isDisabled={{this.isDoneDisabled}}
            @isRunning={{or (eq this.state "uploading") (eq this.state "processing")}}
            class="min-w-32"
            {{on "click" this.handleDone}}
          >
            {{t "slr.reimagined.done"}}
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>
}
